import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

const MaintenanceCalculator = () => {
  const history = useHistory();

  const [serviceCount, setServiceCount] = useState(4);
  const [slaTier, setSlaTier] = useState('4hr'); // '24hr', '4hr', '1hr'
  const [auditFrequency, setAuditFrequency] = useState('quarterly'); // 'biannual', 'quarterly', 'monthly'
  const [devHours, setDevHours] = useState(20);
  const [cloudProvider, setCloudProvider] = useState('AWS & GCP');

  const slaLabels = {
    '24hr': { title: 'Standard 24-Hour SLA', badge: 'ESSENTIAL COVERAGE', desc: 'Ideal for non-critical internal apps & staging' },
    '4hr': { title: 'Priority 4-Hour SLA', badge: 'HIGH AVAILABILITY', desc: 'Recommended for customer-facing production platforms' },
    '1hr': { title: 'Mission-Critical 1-Hour SLA', badge: 'ENTERPRISE 24/7', desc: 'Zero-downtime pager with instant engineer dispatch' }
  };

  const handleLockInRetainer = () => {
    history.push({
      pathname: '/rfp',
      state: {
        prefilledDomain: 'DevOps & Maintenance Retainer',
        prefilledBudget: 'Custom Scope / Need Estimate',
        prefilledScope: `Configured SLA Retainer Scope: ${serviceCount} Services on ${cloudProvider}, ${slaLabels[slaTier]?.title || slaTier}, ${auditFrequency} security audits, and ${devHours} dedicated monthly engineering hours.`
      }
    });
  };

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '3.5rem', paddingBottom: '6rem' }}>
      <div className="container-custom">
        <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 3.5rem auto' }}>
          <div className="pill-badge pill-emerald" style={{ marginBottom: '1.2rem' }}>
            🛡️ PROACTIVE CLOUD RELIABILITY &amp; SRE
          </div>
          <h1 style={{ fontSize: 'clamp(2.2rem, 4.2vw, 3.2rem)', marginBottom: '1rem', fontWeight: 900 }}>
            Maintenance &amp; SLA Retainer <br />
            <span className="gradient-text-blue">Sizing &amp; Scope Planner</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', lineHeight: '1.6' }}>
            Configure your technical parameters to calculate the optimal SLA support package. All retainers are custom-quoted with zero upfront cost or SaaS markup.
          </p>
        </div>

        <div className="row">
          {/* Controls Column */}
          <div className="col-lg-7 mb-4 mb-lg-0">
            <div className="workshop-card" style={{ padding: '2.5rem', backgroundColor: '#ffffff' }}>
              <h3 style={{ fontSize: '1.35rem', marginBottom: '1.8rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span>⚙️</span> Infrastructure &amp; Support Parameters
              </h3>

              {/* Service Count Slider */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Active Production Services / Microservices</span>
                  <span style={{ fontWeight: 800, color: 'var(--accent-blue)', fontSize: '1.1rem' }}>
                    {serviceCount} Services
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={serviceCount}
                  onChange={(e) => setServiceCount(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--accent-blue)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  <span>1 App (Personal / Small MVP)</span>
                  <span>10 Distributed Services</span>
                  <span>20+ Multi-Cloud Cluster</span>
                </div>
              </div>

              {/* SLA Response Time */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                  Target Response SLA (Contract Defined)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.8rem' }}>
                  {[
                    { id: '24hr', label: '24-Hour', desc: 'Standard business hours' },
                    { id: '4hr', label: '4-Hour Priority', desc: 'Accelerated triage' },
                    { id: '1hr', label: '1-Hour 24/7', desc: 'Mission-critical pager' }
                  ].map((tier) => (
                    <button
                      type="button"
                      key={tier.id}
                      onClick={() => setSlaTier(tier.id)}
                      className={`tab-btn ${slaTier === tier.id ? 'active' : ''}`}
                      style={{ textAlign: 'left', padding: '0.8rem 1rem' }}
                    >
                      <div style={{ fontWeight: 800 }}>{tier.label}</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>{tier.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dedicated Engineering Hours */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Dedicated Monthly Engineering Hours</span>
                  <span style={{ fontWeight: 800, color: 'var(--accent-blue)', fontSize: '1.1rem' }}>
                    {devHours} Hours / month
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="60"
                  step="5"
                  value={devHours}
                  onChange={(e) => setDevHours(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--accent-blue)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  <span>5 hrs (Routine Patches)</span>
                  <span>25 hrs (Active Engineering)</span>
                  <span>60 hrs (Dedicated SRE Pod)</span>
                </div>
              </div>

              {/* Security Audits */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                  Security &amp; Vulnerability Audits
                </label>
                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  {[
                    { id: 'biannual', label: 'Bi-Annual Review' },
                    { id: 'quarterly', label: 'Quarterly Deep Scan' },
                    { id: 'monthly', label: 'Continuous Monthly' }
                  ].map((aud) => (
                    <button
                      type="button"
                      key={aud.id}
                      onClick={() => setAuditFrequency(aud.id)}
                      className={`tab-btn ${auditFrequency === aud.id ? 'active' : ''}`}
                      style={{ flex: 1 }}
                    >
                      {aud.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cloud Provider */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                  Cloud Environment
                </label>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  {['AWS', 'GCP', 'AWS & GCP Hybrid', 'Azure', 'DigitalOcean / VPS / Bare Metal'].map((provider) => (
                    <button
                      type="button"
                      key={provider}
                      onClick={() => setCloudProvider(provider)}
                      className={`tab-btn ${cloudProvider === provider ? 'active' : ''}`}
                    >
                      {provider}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sizing & Scope Recommendation Column */}
          <div className="col-lg-5">
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-subtle)',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: 'var(--card-shadow-hover)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%'
            }}>
              <div>
                <div className="pill-badge pill-blue" style={{ marginBottom: '1rem' }}>
                  {slaLabels[slaTier]?.badge || 'TAILORED SLA RETAINER'}
                </div>
                <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>Configured SLA Tier</div>
                <div style={{ fontSize: '1.9rem', fontWeight: 900, color: 'var(--text-primary)', margin: '0.4rem 0 0.8rem 0', fontFamily: 'Outfit', lineHeight: 1.2 }}>
                  {slaLabels[slaTier]?.title}
                </div>
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#ecfdf5',
                  color: '#059669',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  padding: '0.35rem 0.8rem',
                  borderRadius: '999px',
                  border: '1px solid #a7f3d0',
                  marginBottom: '1.5rem'
                }}>
                  Quote on Request • Tailored to Architecture
                </div>

                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1.2rem', marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>Calibrated Retainer Inclusions:</h4>
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.9' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> 24/7 Automated Infrastructure Uptime Monitoring
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> Target {slaTier.toUpperCase()} Incident Response SLA (Per SOW)
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> {devHours} Hours Active Bugfixes, Patches &amp; SRE Support
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> Automated Backup Verification &amp; DR Testing
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> {auditFrequency.charAt(0).toUpperCase() + auditFrequency.slice(1)} Vulnerability &amp; CVE Patch Report
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ color: 'var(--accent-emerald)', fontWeight: 800 }}>✓</span> Direct Private Slack / Engineering Escalation Channel
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <button
                  onClick={handleLockInRetainer}
                  className="btn-primary-blue"
                  style={{ width: '100%', justifyContent: 'center', padding: '0.95rem', fontSize: '1rem', marginBottom: '0.8rem' }}
                >
                  Request Tailored Retainer Proposal →
                </button>
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Month-to-month flexibility &bull; 30-day cancellation &bull; SLA Defined in SOW
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceCalculator;
